import * as stylex from '@stylexjs/stylex';

const MEDIA_MOBILE = '@media (max-width: 768px)';
const MEDIA_SMALL_MOBILE = '@media (max-width: 480px)';

export const controlNetStyles = stylex.create({
    contentSection: {
        padding: {
            default: '2rem 0',
            [MEDIA_SMALL_MOBILE]: '1rem 0',
        },
    },
    /* Section Titles */
    sectionTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 var(--spacing-xl) 0',
        color: 'var(--text-primary)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 60,
            height: 3,
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        },
    },
    sectionSubtitle: {
        fontSize: '1.25rem',
        fontWeight: 500,
        marginBottom: '0.75rem',
        color: 'var(--text-primary)',
    },
    contentList: {
        lineHeight: 1.8,
        color: 'var(--text-secondary)',
        marginBottom: '2rem',
    },
    divider: {
        borderWidth: 0,
        borderTopWidth: '1px',
        borderStyle: 'solid',
        borderTopColor: 'var(--border)',
        margin: '2rem 0',
    },
    featuresSection: {
        marginBottom: {
            default: '4rem',
            [MEDIA_SMALL_MOBILE]: '2rem',
        },
    },
    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(300px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
        gap: '2rem',
        marginBottom: '2rem',
    },
    featureCard: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        padding: '2rem',
        textAlign: 'left',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: {
            default: 'var(--border)',
            ':hover': 'var(--primary)',
        },
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        transform: {
            default: 'none',
            ':hover': 'translateY(-4px)',
        },
        boxShadow: {
            default: 'none',
            ':hover': 'var(--shadow-md)',
        },
    },
    featureIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3rem',
        height: '3rem',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: 'var(--primary)',
        borderRadius: '50%',
        marginBottom: '1rem',
    },
    gettingStartedSection: {
        marginBottom: {
            default: '4rem',
            [MEDIA_SMALL_MOBILE]: '2rem',
        },
    },
    gettingStartedContent: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(300px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
        gap: '2rem',
    },
    stepCard: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        padding: '3.5rem 2rem 2rem 2rem',
        textAlign: 'left',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transitionProperty: 'transform, box-shadow',
        transitionDuration: 'var(--transition-normal)',
        marginTop: '1.5rem',
        ':hover': {
            transform: 'translateY(-4px)',
            boxShadow: 'var(--shadow-md)',
        },
    },
    stepNumber: {
        position: 'absolute',
        top: '-1.5rem',
        left: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3rem',
        height: '3rem',
        backgroundColor: 'hsl(var(--hue-accent),90%,60%)',
        color: 'var(--text-on-accent)',
        borderRadius: '50%',
        fontSize: '1.25rem',
        fontWeight: 700,
        boxShadow: 'var(--shadow-md)',
        zIndex: 1,
    },
    testSection: {
        marginBottom: {
            default: '4rem',
            [MEDIA_SMALL_MOBILE]: '2rem',
        },
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: {
            default: 'var(--border)',
            ':hover': 'var(--primary)',
        },
        borderRadius: 'var(--radius-lg)',
        padding: {
            default: '2rem',
            [MEDIA_MOBILE]: '1.5rem',
            [MEDIA_SMALL_MOBILE]: '1rem',
        },
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        boxShadow: {
            default: 'none',
            ':hover': 'var(--shadow-md)',
        },
    },
    testHeader: {
        display: 'flex',
        flexDirection: {
            default: 'row',
            [MEDIA_MOBILE]: 'column',
        },
        alignItems: {
            default: 'center',
            [MEDIA_MOBILE]: 'flex-start',
        },
        gap: '1rem',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottomWidth: '2px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
    },
    testBadge: {
        backgroundColor: 'var(--primary)',
        color: 'var(--text-on-accent)',
        padding: {
            default: '0.5rem 1rem',
            [MEDIA_MOBILE]: '0.4rem 0.8rem',
        },
        borderRadius: 'var(--radius-full)',
        fontSize: {
            default: '0.875rem',
            [MEDIA_MOBILE]: '0.75rem',
        },
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
    },
    testTitle: {
        fontSize: {
            default: '1.5rem',
            [MEDIA_MOBILE]: '1.25rem',
            [MEDIA_SMALL_MOBILE]: '1.1rem',
        },
        fontWeight: 600,
        margin: 0,
        color: 'var(--text-primary)',
        flexGrow: 1,
    },
    testMeta: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(200px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    metaItem: {
        backgroundColor: 'var(--bg)',
        padding: '0.75rem 1rem',
        borderRadius: 'var(--radius-md)',
        borderLeftWidth: '3px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'var(--primary)',
    },
    metaLabel: {
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'var(--text-secondary)',
        marginBottom: '0.25rem',
        fontWeight: 600,
    },
    metaValue: {
        fontSize: '0.95rem',
        color: 'var(--text-primary)',
        fontWeight: 500,
    },
    promptSection: {
        backgroundColor: 'var(--bg)',
        padding: {
            default: '1.5rem',
            [MEDIA_MOBILE]: '1rem',
        },
        borderRadius: 'var(--radius-md)',
        marginBottom: '1.5rem',
    },
    promptLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        '::before': {
            content: '"💬"',
            fontSize: '1rem',
        },
    },
    codeBlock: {
        backgroundColor: 'var(--bg-card)',
        padding: {
            default: '1rem',
            [MEDIA_MOBILE]: '0.75rem',
        },
        borderRadius: '0.5rem',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        color: 'var(--text-secondary)',
        fontSize: {
            default: '0.875rem',
            [MEDIA_MOBILE]: '0.75rem',
        },
        overflowX: 'auto',
        margin: 0,
        whiteSpace: 'pre-wrap',
        fontFamily: '"Courier New", monospace',
    },
    imageGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(300px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
        gap: {
            default: '1.5rem',
            [MEDIA_MOBILE]: '1rem',
        },
        marginBottom: '2rem',
    },
    threeColumns: {
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(250px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
    },
    imageContainer: {
        textAlign: 'center',
    },
    image: {
        width: '100%',
        borderRadius: '0.5rem',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        transitionProperty: 'transform',
        transitionDuration: 'var(--transition-normal)',
        ':hover': {
            transform: 'scale(1.02)',
        },
    },
    imageLabel: {
        marginTop: '0.5rem',
        color: 'var(--text-secondary)',
        fontWeight: 500,
        fontSize: '0.9rem',
    },
    variantsGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(300px, 1fr))',
            [MEDIA_MOBILE]: '1fr',
        },
        gap: '1.5rem',
    },
    variantCard: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        height: '100%',
    },
    variantCardPrimary: {
        borderColor: 'var(--primary-light)',
        backgroundColor: 'var(--bg-subtle-accent)',
    },
    variantHeader: {
        padding: '1.25rem',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    variantTitle: {
        margin: 0,
        fontSize: '1.1rem',
        fontWeight: 600,
    },
    variantFeatures: {
        padding: '1.25rem',
        margin: 0,
        listStyleType: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    variantFeatureItem: {
        position: 'relative',
        paddingLeft: '1.5rem',
        lineHeight: 1.5,
        color: 'var(--text-secondary)',
        fontSize: '0.95rem',
        '::before': {
            content: '"•"',
            position: 'absolute',
            left: 0,
            color: 'var(--primary)',
            fontWeight: 'bold',
        },
    },
});
